import useSWR, { SWRConfiguration, SWRResponse } from 'swr';

import { REFRESH_INTERVAL as refreshInterval } from '@/constants/rpc';

import { getBaseURL, isTest } from '@/utils/environment';
import { GasInfo, GasInfoRaw } from '@/utils/gas';

const gasFetcher = async (path: string) => {
  // Next.js requires that {fetch} uses an absolute URL. This is because the
  // application doesn't know what context this code will be called in (either
  // in the browser on production or in node when running tests/SSR). This means
  // the implementation of {fetch} can vary between them, and so an absolute
  // URL is necessary to avoid ambiguity.
  const response: GasInfoRaw = await fetch(isTest ? getBaseURL() + path : path).then((r) =>
    r.json(),
  );
  const { custom, suggested, type } = response;

  if (type === 'legacy') {
    return {
      type: 'legacy',
      custom: BigInt(custom),
      suggested: BigInt(suggested),
    } as GasInfo;
  }

  return {
    type,
    suggested: {
      maxFeePerGas: BigInt(suggested.maxFeePerGas),
      baseFeePerGas: BigInt(suggested.baseFeePerGas),
      maxPriorityFeePerGas: BigInt(suggested.maxPriorityFeePerGas),
    },
    custom: {
      maxFeePerGas: BigInt(custom.maxFeePerGas),
      baseFeePerGas: BigInt(custom.baseFeePerGas),
      maxPriorityFeePerGas: BigInt(custom.maxPriorityFeePerGas),
    },
  } as GasInfo;
};
/**
 * Hook to fetch gas fees for type 1 & 2 transactions.
 *
 * @param chainId - Network ID of the chain.
 * @param options - An SWR configuration object.
 *
 * @remarks
 *
 * Defaults to refetching every 30 seconds (but it can be overriden).
 */
const useGasOracle = (
  chainId: number | undefined,
  options?: SWRConfiguration,
): SWRResponse<GasInfo, Error> =>
  useSWR(chainId ? `/api/gas?chainId=${chainId}` : undefined, gasFetcher, {
    refreshInterval,
    ...(options ?? {}),
  });

export default useGasOracle;
